import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useTranslations} from '@/i18n/hooks/useTranslations'
import React, {useMemo, useState} from 'react'

interface Props {
  locale?: string
  userId?: string
}

interface FormValues {
  email: string
  phoneNumber: string
  boxNumber: string
  problemType: string
  message: string
}

const PROBLEM_TYPE_KEYS = ['cantStartRental', 'cantEndRental', 'boxCompartmentIssue', 'billingPaymentIssue', 'appTechnicalBug', 'other']

export const SupportContactForm: React.FC<Props> = ({locale, userId}) => {
  const t = useTranslations()
  const [formValues, setFormValues] = useState<FormValues>({
    email: '',
    phoneNumber: '',
    boxNumber: '',
    problemType: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const problemTypeOptions = useMemo(
    () =>
      PROBLEM_TYPE_KEYS.map((value) => ({
        value,
        label: t(`contact.supportForm.problemTypes.${value}`),
      })),
    [t],
  )

  const updateField = (field: keyof FormValues, value: string) => {
    setFormValues((prevValues) => ({...prevValues, [field]: value}))
    if (errorMessage) setErrorMessage('')
    if (isSuccess) setIsSuccess(false)
  }

  const validateForm = () => {
    const email = formValues.email.trim()
    const phoneNumber = formValues.phoneNumber.trim()
    const message = formValues.message.trim()

    if (!email || !formValues.problemType || !message) {
      setErrorMessage(t('contact.supportForm.errors.required'))
      return false
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(email)) {
      setErrorMessage(t('contact.supportForm.errors.invalidEmail'))
      return false
    }

    const phoneNumberPattern = /^[+\d()\-.\s]+$/
    const phoneDigitsCount = phoneNumber.replace(/\D/g, '').length
    if (!phoneNumber || !phoneNumberPattern.test(phoneNumber) || phoneDigitsCount < 7) {
      setErrorMessage(t('contact.supportForm.errors.invalidPhoneNumber'))
      return false
    }

    if (message.length < 20) {
      setErrorMessage(t('contact.supportForm.errors.messageTooShort'))
      return false
    }

    return true
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    setErrorMessage('')

    try {
      const response = await fetch('/api/support', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          email: formValues.email.trim(),
          phoneNumber: formValues.phoneNumber.trim(),
          boxNumber: formValues.boxNumber.trim(),
          problemType: formValues.problemType,
          message: formValues.message.trim(),
          locale: locale ?? 'sv',
          userId,
          client: 'web',
        }),
      })

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfterValue = response.headers.get('Retry-After')
          const retryAfterSeconds = Number.parseInt(retryAfterValue ?? '', 10)

          if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            setErrorMessage(t('contact.supportForm.errors.tooManyRequestsWithRetry', {seconds: retryAfterSeconds}))
            return
          }

          setErrorMessage(t('contact.supportForm.errors.tooManyRequests'))
          return
        }

        throw new Error('Request failed')
      }

      setFormValues({
        email: '',
        phoneNumber: '',
        boxNumber: '',
        problemType: '',
        message: '',
      })
      setIsSuccess(true)
    } catch {
      setErrorMessage(t('contact.supportForm.errors.submitFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-court-600" htmlFor="support-email">
          {t('contact.supportForm.emailLabel')} <span className="text-red-600">*</span>
        </label>
        <input
          id="support-email"
          type="email"
          value={formValues.email}
          onChange={(event) => updateField('email', event.target.value)}
          placeholder={t('contact.supportForm.emailPlaceholder')}
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-piffl-pink-500"
          autoComplete="email"
          required
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-court-600" htmlFor="support-phone-number">
          {t('contact.supportForm.phoneNumberLabel')} <span className="text-red-600">*</span>
        </label>
        <input
          id="support-phone-number"
          type="tel"
          value={formValues.phoneNumber}
          onChange={(event) => updateField('phoneNumber', event.target.value)}
          placeholder={t('contact.supportForm.phoneNumberPlaceholder')}
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-piffl-pink-500"
          autoComplete="tel"
          required
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-court-600" htmlFor="support-box-number">
          {t('contact.supportForm.boxNumberLabel')}
        </label>
        <input
          id="support-box-number"
          type="text"
          value={formValues.boxNumber}
          onChange={(event) => updateField('boxNumber', event.target.value)}
          placeholder={t('contact.supportForm.boxNumberPlaceholder')}
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 placeholder:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-piffl-pink-500"
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-court-600" htmlFor="support-problem-type">
          {t('contact.supportForm.problemTypeLabel')} <span className="text-red-600">*</span>
        </label>
        <Select value={formValues.problemType} onValueChange={(value) => updateField('problemType', value)}>
          <SelectTrigger id="support-problem-type" className="w-full">
            <SelectValue placeholder={t('contact.supportForm.problemTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {problemTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-court-600" htmlFor="support-message">
          {t('contact.supportForm.messageLabel')} <span className="text-red-600">*</span>
        </label>
        <textarea
          id="support-message"
          value={formValues.message}
          onChange={(event) => updateField('message', event.target.value)}
          placeholder={t('contact.supportForm.messagePlaceholder')}
          rows={6}
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-piffl-pink-500"
          minLength={20}
          required
        />
      </div>

      {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
      {isSuccess ? <p className="text-sm text-green-700">{t('contact.supportForm.successMessage')}</p> : null}

      <div className="flex justify-end">
        <Button type="submit" size="lg" className="w-full md:w-auto px-8 py-2 cursor-pointer " disabled={isSubmitting}>
          {isSubmitting ? t('contact.supportForm.submittingButton') : t('contact.supportForm.submitButton')}
        </Button>
      </div>
    </form>
  )
}
